
  import { computed, defineComponent } from 'vue';
  import { CheckboxField, DateTimeField, DropdownField, InputField } from '@/ui/components/form';
  import { useRepeatOptions } from '@/ui/concerns/rrule/useRepeatOptions';
  import { EditableRule } from '@/entities/rrule/model';
  import { Days, Repeat } from '@/entities/rrule/constants';
  import { fromUnixTime, getUnixTime } from 'date-fns';
  import { user } from '@/ui/auth/auth';
  import { useTimeZonedDates } from '@/ui/concerns/useTimeZonedDates';

  interface RRuleProps {
    fieldsDisabled: boolean;
    rule: EditableRule;
    withDueDate: boolean;
    dueIn?: number;
    availableFromLabel?: string;
    repeatsLabel?: string;
  }

  export default defineComponent<RRuleProps>({
    name: 'RRule',
    components: {
      DateTimeField,
      DropdownField,
      InputField,
      CheckboxField,
    },
    props: ['fieldsDisabled', 'rule', 'withDueDate', 'dueIn', 'availableFromLabel', 'repeatsLabel'],
    setup(props, ctx) {
      const {
        dayOptions,
        dayFrequencyOptions,
        frequencyOptions,
        monthPositionOptions,
        repeatOptions,
      } = useRepeatOptions();

      const { getDateInUserTimeZone, getDateInLocalTimeZone } = useTimeZonedDates();

      const ruleStart = computed(() => getDateInUserTimeZone(props.rule.start!));

      const dueAt = computed(() => fromUnixTime(
        getUnixTime(ruleStart.value) + ((props.dueIn ?? 60) * 60),
      ));

      const setRepeat = (value: string) => {
        ctx.emit('update-repeat', value);
      };

      const setFrequency = (value: string) => {
        ctx.emit('update-frequency', value);
      };

      const updateRuleInterval = (value: number) => {
        ctx.emit('update-rule-interval', value);
      };

      const changeDay = (value: boolean, day: Days) => {
        ctx.emit('update-day', { value, day });
      };

      const updateMonthPosition = (value: number) => {
        ctx.emit('update-month-position', value);
      };

      const updateMonthDaysFrequency = (value: string) => {
        ctx.emit('update-month-days-frequency', value);
      };

      const updateRuleStart = (value: Date) => {
        ctx.emit('update-rule-start', getDateInLocalTimeZone(value));
      };

      const handleDueAtChange = (value: Date) => {
        ctx.emit('update-due-at', getDateInLocalTimeZone(value));
      };

      return {
        ruleStart,
        dueAt,
        dayOptions,
        dayFrequencyOptions,
        frequencyOptions,
        monthPositionOptions,
        repeatOptions,
        Days,
        Repeat,
        setRepeat,
        setFrequency,
        updateRuleInterval,
        changeDay,
        updateMonthPosition,
        updateMonthDaysFrequency,
        updateRuleStart,
        handleDueAtChange,
        locale: user.value.locale,
      };
    },
  });
